// HRMS — Dashboard (live KPIs).
import React, { useEffect, useState } from 'react';
import hr from '../api';
import { PageHeader, Kpi, Pill, fmtDate, fmtDateTime, SOFT, PANEL, BSC_ORANGE } from '../ui';

export default function Dashboard({ companyId, onNav }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!companyId) return;
    (async () => {
      try { setD(await hr.get('/dashboard', { company_id: companyId })); }
      catch (e) { console.error(e); }
    })();
  }, [companyId]);
  if (!d) return <div className="text-white/40 text-sm">Loading dashboard…</div>;
  return (
    <div data-testid="hr-dashboard">
      <PageHeader title="People HQ" subtitle="Live workforce snapshot" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Total Employees" value={d.totals.total} sub={`${d.totals.active} active · ${d.totals.probation} probation · ${d.totals.notice} notice`} testid="kpi-total" />
        <Kpi label="Present Today" value={d.attendance_today.present} sub={`${d.attendance_today.present_pct}% attendance`} tone="green" testid="kpi-present" />
        <Kpi label="On Leave Today" value={d.attendance_today.on_leave} tone="orange" testid="kpi-leave" />
        <Kpi label="Absent Today" value={d.attendance_today.absent} tone={d.attendance_today.absent > 0 ? 'red' : 'neutral'} testid="kpi-absent" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
        <Kpi label="Pending Leaves" value={d.pending.leaves} tone={d.pending.leaves ? 'orange' : 'neutral'} testid="kpi-pending-leaves" />
        <Kpi label="Pending Reimbursements" value={d.pending.reimbursements} tone={d.pending.reimbursements ? 'orange' : 'neutral'} testid="kpi-pending-reimb" />
        <Kpi label="Open Jobs" value={d.hiring.open_jobs} sub={`${d.hiring.active_candidates} candidates`} tone="blue" testid="kpi-jobs" />
        <Kpi label="This Month" value={`🎂 ${d.celebrations.birthdays_this_month} · 🎉 ${d.celebrations.anniversaries_this_month}`} sub="birthdays · anniversaries" testid="kpi-celebrations" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
        <div className={`${PANEL} rounded-xl p-5`} data-testid="hr-dept-breakdown">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white font-medium">Headcount by Department</h3>
            <button onClick={() => onNav?.('org')} className="text-xs text-white/40 hover:text-white">View →</button>
          </div>
          {!d.departments.length ? <p className="text-white/40 text-sm italic">No departments yet.</p> :
            d.departments.slice(0, 8).map((x, i) => {
              const max = Math.max(...d.departments.map(z => z.count));
              const pct = (x.count / max) * 100;
              return (
                <div key={i} className="py-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-white/80">{x.name}</span>
                    <span className="text-white tabular-nums">{x.count}</span>
                  </div>
                  <div className="mt-1 h-1.5 bg-white/5 rounded-full overflow-hidden">
                    <div style={{ width: `${pct}%`, backgroundColor: BSC_ORANGE }} className="h-full" />
                  </div>
                </div>
              );
            })
          }
        </div>

        <div className={`${PANEL} rounded-xl p-5`} data-testid="hr-announcements">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white font-medium">Latest Announcements</h3>
            <button onClick={() => onNav?.('announcements')} className="text-xs text-white/40 hover:text-white">View →</button>
          </div>
          {!d.recent_announcements.length ? <p className="text-white/40 text-sm italic">Nothing yet.</p> :
            d.recent_announcements.map((a) => (
              <div key={a.id} className="py-2 border-t border-white/5">
                <div className="text-sm text-white">{a.title}{a.pinned && <Pill tone="orange">Pinned</Pill>}</div>
                <div className="text-xs text-white/40 mt-0.5">{fmtDateTime(a.created_at)} · {a.created_by}</div>
              </div>
            ))
          }
        </div>
      </div>
    </div>
  );
}
